import { Link } from 'react-router-dom';
import ResourceLayout from '../../components/resources/ResourceLayout';
import useSEO from '../../hooks/useSEO';
import { canonicalUrl } from '../../utils/testSite';
import { schemaGraph } from '../../utils/schema';
import { getStateBySlug } from '../../utils/states';
import { KANSAS_GUIDES } from './kansasGuides';

const KS = getStateBySlug('kansas');
const UPDATED_LABEL = 'Updated September 2026 · Verified against krec.ks.gov';
const PUBLISHED = '2026-09-27';

// Pages outside KANSAS_GUIDES that a guide can list as related.
const EXTRA_RELATED = {
  'kansas-real-estate-ce-requirements': { title: 'Kansas CE Requirements 2026', desc: 'The complete 12-hour breakdown, deadlines and fees.' },
};

function relatedFor(guide) {
  const items = (guide.related || []).map(slug => {
    const g = KANSAS_GUIDES.find(x => x.slug === slug);
    if (g) return { url: `/${g.slug}`, title: g.title, desc: g.cardDesc };
    const extra = EXTRA_RELATED[slug];
    return extra && { url: `/${slug}`, ...extra };
  }).filter(Boolean);
  // Always offer the way back to the hub.
  return [{ url: '/kansas/resources', title: 'All Kansas CE Guides', desc: 'Quick facts, common questions, and every Kansas guide in one place.' }, ...items];
}

// Shared template for the Kansas guides defined in kansasGuides.jsx.
export default function KansasGuidePage({ guide }) {
  const url = canonicalUrl(`/${guide.slug}`);

  const mainSchemas = [{
    '@type': 'Article',
    headline: guide.title,
    description: guide.description,
    datePublished: guide.datePublished || PUBLISHED,
    dateModified: guide.dateModified || PUBLISHED,
    author: { '@type': 'Organization', name: 'Steady Education' },
    publisher: { '@id': 'https://steadyeducation.com/#organization' },
    mainEntityOfPage: url,
  }];
  if (guide.faq) {
    mainSchemas.push({
      '@type': 'FAQPage',
      mainEntity: guide.faq.map(f => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  useSEO({
    title: guide.seoTitle,
    description: guide.description,
    url,
    image: 'https://steadyeducation.com/og/kansas.png',
    imageAlt: 'Kansas real estate CE guides from Steady Education',
    type: 'article',
    jsonLd: schemaGraph({
      mainSchemas,
      breadcrumb: [
        { name: 'Home', url: 'https://steadyeducation.com' },
        { name: 'Kansas', url: 'https://steadyeducation.com/kansas' },
        { name: 'Resources', url: 'https://steadyeducation.com/kansas/resources' },
        { name: guide.breadcrumb, url },
      ],
    }),
  });

  return (
    <ResourceLayout
      state={KS}
      crumbs={[{ label: 'Kansas', href: '/kansas' }, { label: 'Resources', href: '/kansas/resources' }]}
      updatedLabel={guide.updatedLabel || UPDATED_LABEL}
      ctaSub="All 12 KREC-required hours"
      ctaPoints={[
        'The mandatory 3-hour Required Core',
        'Three 3-hour electives',
        '100% online, self-paced',
        'We report your completion to KREC',
      ]}
      breadcrumb={guide.breadcrumb}
      title={guide.title}
      subtitle={guide.subtitle}
      relatedGuides={relatedFor(guide)}
    >
      {guide.body(guide)}

      <div style={inlineCta}>
        <strong>Complete your Kansas CE with Steady Education.</strong> All 12 KREC-required hours — the Kansas Required Core plus three electives — for ${KS.packagePrice}, or ${KS.coursePrice} per course. Self-paced, online, and we report your completion to KREC for you. Kansas School ID {KS.schoolLicense}.
        <div style={{ marginTop: 14 }}>
          <Link to="/kansas/courses" style={inlineCtaBtn}>See the Kansas CE package →</Link>
        </div>
      </div>
    </ResourceLayout>
  );
}

const inlineCta = { background: '#FAF7F2', border: '1px solid #d4c5ad', borderRadius: 12, padding: '20px 24px', marginTop: 24 };
const inlineCtaBtn = { display: 'inline-block', background: '#2C453E', color: 'white', padding: '10px 22px', borderRadius: 8, textDecoration: 'none', fontWeight: 700, fontSize: 14 };
